import React, { useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
const { FiClock, FiTag, FiArrowLeft, FiArrowRight } = FiIcons;
import SafeIcon from '../../common/SafeIcon';
import Navigation from '../../components/Navigation';
import Footer from '../../components/Footer';
import TableOfContents from '../../components/TableOfContents';
import SEO from '../shared/SEO';
import AdUnit from '../../components/AdUnit';
import { useTheme } from '../../hooks/useTheme';
import articleData from '../../data/articleData';
import { generateArticlePageSchema } from '../../lib/seo';
import { AD_POSITIONS } from '../../utils/adConfig';

import Article1 from '../../articles/Article1';
import Article2 from '../../articles/Article2';
import Article3 from '../../articles/Article3';
import Article4 from '../../articles/Article4';
import Article5 from '../../articles/Article5';
import Article6 from '../../articles/Article6';

const articleComponents = { 1: Article1, 2: Article2, 3: Article3, 4: Article4, 5: Article5, 6: Article6 };

const SITE_URL = 'https://omakase.tw';

function ArticlePage() {
  const { slug }    = useParams();
  const navigate    = useNavigate();
  const { theme }   = useTheme();
  const contentRef  = useRef(null);

  const article      = articleData.find(a => a.slug === slug);
  const currentIndex = article ? articleData.findIndex(a => a.id === article.id) : -1;
  const prevArticle  = currentIndex > 0 ? articleData[currentIndex - 1] : null;
  const nextArticle  = currentIndex < articleData.length - 1 ? articleData[currentIndex + 1] : null;

  useEffect(() => {
    if (!article) navigate('/404', { replace: true });
  }, [article, navigate]);

  useEffect(() => {
    if (article) window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, [slug, article]);

  if (!article) return null;

  const ArticleContent = articleComponents[article.id];

  const handleArticleNavigation = (targetSlug) => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    navigate(`/${targetSlug}`);
  };

  // ✅ 修正：明確傳入 slug 給 schema 物件，確保 breadcrumb @id 正確生成
  // 之前：schema 物件中沒有 slug → breadcrumb @id fallback 為 /#breadcrumb
  // 修正後：slug 傳入 → breadcrumb @id = https://omakase.tw/{slug}#breadcrumb
  const combinedSchema = generateArticlePageSchema(
    {
      title:         article.title,
      description:   article.excerpt,
      datePublished: article.publishDate,
      dateModified:  article.modifiedDate,
      author:        article.author,
      image:         article.image,
      url:           `/${article.slug}`,
      slug:          article.slug,          // ✅ 新增：確保 breadcrumb @id 唯一
      category:      article.category,
      metaKeywords:  article.metaKeywords,
      section:       article.category,
    },
    [
      { name: '首頁',        url: `${SITE_URL}/` },
      { name: '深度文章',    url: `${SITE_URL}/articles` },
      { name: article.title, url: `${SITE_URL}/${article.slug}` },
    ]
  );

  return (
    <>
      <SEO
        title={article.metaTitle || article.title}
        description={article.metaDescription || article.excerpt}
        keywords={article.metaKeywords || article.tags.join(',')}
        image={article.image}
        url={`/${article.slug}`}
        type="article"
        schema={combinedSchema}
        article={{
          publishDate:  article.publishDate,
          modifiedDate: article.modifiedDate,
          author:       article.author,
          section:      article.category,
          tags:         article.tags,
        }}
      />

      <div className={`min-h-screen ${theme === 'dark' ? 'dark' : ''}`}>
        <Navigation />

        <article className="pt-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">

            {/* 文章標頭 */}
            <motion.div
              key={slug}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="max-w-4xl mx-auto mb-12"
            >
              {/* ✅ 麵包屑導航（可見 UI + HTML microdata） */}
              <nav aria-label="麵包屑" className="mb-6">
                <ol
                  className="flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400"
                  itemScope
                  itemType="https://schema.org/BreadcrumbList"
                >
                  <li itemScope itemProp="itemListElement" itemType="https://schema.org/ListItem">
                    <button
                      onClick={() => navigate('/')}
                      className="hover:text-orange-500 transition-colors"
                      itemProp="item"
                    >
                      <span itemProp="name">首頁</span>
                    </button>
                    <meta itemProp="position" content="1" />
                  </li>
                  <li aria-hidden="true" className="select-none">/</li>
                  <li itemScope itemProp="itemListElement" itemType="https://schema.org/ListItem">
                    <button
                      onClick={() => navigate('/articles')}
                      className="hover:text-orange-500 transition-colors"
                      itemProp="item"
                    >
                      <span itemProp="name">深度文章</span>
                    </button>
                    <meta itemProp="position" content="2" />
                  </li>
                  <li aria-hidden="true" className="select-none">/</li>
                  <li
                    className="text-slate-700 dark:text-slate-200 truncate max-w-xs"
                    aria-current="page"
                    itemScope
                    itemProp="itemListElement"
                    itemType="https://schema.org/ListItem"
                  >
                    <span itemProp="name">{article.title}</span>
                    <meta itemProp="position" content="3" />
                  </li>
                </ol>
              </nav>

              <div className="mb-6">
                <span className="inline-block px-4 py-2 bg-orange-500/10 text-orange-500 rounded-full text-sm font-medium mb-4">
                  {article.category}
                </span>
                <h1 className="text-4xl md:text-5xl font-bold text-slate-900 dark:text-white mb-6 leading-tight">
                  {article.title}
                </h1>
                <p className="text-xl text-slate-600 dark:text-slate-400 mb-6 article-summary">
                  {article.excerpt}
                </p>

                <div className="flex flex-wrap items-center gap-6 text-sm text-slate-600 dark:text-slate-400">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-slate-900 dark:text-white">{article.author}</span>
                    <span>•</span>
                    <span>發布：</span>
                    <time dateTime={article.publishDate}>
                      {new Date(article.publishDate).toLocaleDateString('zh-TW')}
                    </time>
                    {article.modifiedDate && article.modifiedDate !== article.publishDate && (
                      <>
                        <span>•</span>
                        <span>更新：
                          <time dateTime={article.modifiedDate}>
                            {new Date(article.modifiedDate).toLocaleDateString('zh-TW')}
                          </time>
                        </span>
                      </>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <SafeIcon icon={FiClock} size={18} />
                    <span>{article.readTime}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <SafeIcon icon={FiTag} size={18} />
                    <div className="flex gap-2 flex-wrap">
                      {article.tags.map(tag => (
                        <span key={tag} className="text-orange-500">#{tag}</span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* 廣告 - 文章開頭 */}
            <div className="max-w-4xl mx-auto mb-8">
              <AdUnit {...AD_POSITIONS.ARTICLE.top} />
            </div>

            {/* 文章內容 + 側邊欄 */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
              <motion.div
                key={`content-${slug}`}
                ref={contentRef}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="lg:col-span-8"
              >
                <div className="prose prose-lg dark:prose-invert max-w-none
                              prose-headings:text-slate-900 dark:prose-headings:text-white
                              prose-p:text-slate-600 dark:prose-p:text-slate-400
                              prose-a:text-orange-500 hover:prose-a:text-orange-600
                              prose-strong:text-slate-900 dark:prose-strong:text-white
                              prose-code:text-orange-500 dark:prose-code:text-orange-400
                              prose-pre:bg-slate-800 dark:prose-pre:bg-slate-900">
                  {ArticleContent && <ArticleContent />}
                </div>

                <div className="my-12">
                  <AdUnit {...AD_POSITIONS.ARTICLE.middle} />
                </div>
              </motion.div>

              <motion.aside
                key={`sidebar-${slug}`}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.3 }}
                className="lg:col-span-4"
              >
                <div className="sticky top-24 space-y-8">
                  <TableOfContents contentRef={contentRef} />
                  <AdUnit {...AD_POSITIONS.ARTICLE.sidebar} />
                </div>
              </motion.aside>
            </div>

            {/* 廣告 - 文章結尾 */}
            <div className="max-w-4xl mx-auto mt-8">
              <AdUnit {...AD_POSITIONS.ARTICLE.bottom} />
            </div>

            {/* 上一篇 / 下一篇 */}
            <motion.nav
              key={`navigation-${slug}`}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              aria-label="文章導航"
              className="max-w-4xl mx-auto mt-16 pt-8 border-t-2 border-slate-200 dark:border-slate-700"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {prevArticle && (
                  <button
                    onClick={() => handleArticleNavigation(prevArticle.slug)}
                    className="group p-6 bg-white/60 dark:bg-slate-800/60 backdrop-blur-sm rounded-2xl border-2 border-slate-200 dark:border-slate-700 hover:border-orange-500 dark:hover:border-orange-400 transition-all text-left"
                  >
                    <div className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400 mb-2">
                      <SafeIcon icon={FiArrowLeft} size={16} />
                      <span>上一篇</span>
                    </div>
                    <h2 className="font-bold text-slate-900 dark:text-white group-hover:text-orange-500 transition-colors">
                      {prevArticle.title}
                    </h2>
                  </button>
                )}
                {nextArticle && (
                  <button
                    onClick={() => handleArticleNavigation(nextArticle.slug)}
                    className="group p-6 bg-white/60 dark:bg-slate-800/60 backdrop-blur-sm rounded-2xl border-2 border-slate-200 dark:border-slate-700 hover:border-orange-500 dark:hover:border-orange-400 transition-all text-right md:ml-auto"
                  >
                    <div className="flex items-center justify-end gap-2 text-sm text-slate-600 dark:text-slate-400 mb-2">
                      <span>下一篇</span>
                      <SafeIcon icon={FiArrowRight} size={16} />
                    </div>
                    <h2 className="font-bold text-slate-900 dark:text-white group-hover:text-orange-500 transition-colors">
                      {nextArticle.title}
                    </h2>
                  </button>
                )}
              </div>
            </motion.nav>
          </div>
        </article>

        <Footer />
      </div>
    </>
  );
}

export default ArticlePage;